import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReviewsWidgetType } from '@clareefai/types';

export interface PreviewReview {
  id: string;
  promoterName: string;
  promoterCompany?: string | null;
  rating?: number | null;
  comment?: string | null;
  vendorResponse?: string | null;
}

interface ReviewsWidgetPreviewProps {
  widgetType: ReviewsWidgetType;
  heading: string | null;
  accentColor: string | null;
  showCompanyResponse: boolean;
  ctaLabel: string | null;
  companyName?: string | null;
  reviews: PreviewReview[];
  averageRating: number;
  totalReviews: number;
  emptyLabel: string;
}

const Stars: React.FC<{ value: number; size?: number }> = ({ value, size = 14 }) => (
  <span className="inline-flex gap-0.5">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        style={{ width: size, height: size }}
        className={i < Math.round(value) ? 'text-yellow-400 fill-current' : 'text-gray-300'}
      />
    ))}
  </span>
);

const initials = (name: string) =>
  name.trim().split(/\s+/).map((n) => n[0]).join('').substring(0, 2).toUpperCase() || '?';

const Card: React.FC<{ review: PreviewReview; accent: string; showResponse: boolean; companyName?: string | null }> = ({
  review,
  accent,
  showResponse,
  companyName,
}) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
    <div className="flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
        style={{ background: accent }}
      >
        {initials(review.promoterName)}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-gray-900 text-sm">{review.promoterName}</span>
          {typeof review.rating === 'number' && <Stars value={review.rating} size={12} />}
        </div>
        {review.promoterCompany && <p className="text-xs text-gray-500 mt-0.5">{review.promoterCompany}</p>}
      </div>
    </div>
    {review.comment && <p className="text-sm text-gray-700 leading-relaxed mt-3">{review.comment}</p>}
    {showResponse && review.vendorResponse && (
      <div className="mt-3 pl-3 py-2 border-l-[3px] rounded-r-lg bg-indigo-50/60" style={{ borderColor: accent }}>
        <div className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: accent }}>
          {companyName || 'Response'}
        </div>
        <div className="text-xs text-gray-600 leading-relaxed">{review.vendorResponse}</div>
      </div>
    )}
  </div>
);

/** In-page mock of the embedded reviews widget — mirrors reviews-v1.js rendering. */
const ReviewsWidgetPreview: React.FC<ReviewsWidgetPreviewProps> = ({
  widgetType,
  heading,
  accentColor,
  showCompanyResponse,
  ctaLabel,
  companyName,
  reviews,
  averageRating,
  totalReviews,
  emptyLabel,
}) => {
  const accent = accentColor || '#4F46E5';
  const [idx, setIdx] = useState(0);
  const safeIdx = reviews.length ? idx % reviews.length : 0;

  const body = () => {
    if (reviews.length === 0) {
      return <p className="text-sm text-gray-400 text-center py-10">{emptyLabel}</p>;
    }
    if (widgetType === 'grid') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {reviews.map((r) => (
            <Card key={r.id} review={r} accent={accent} showResponse={showCompanyResponse} companyName={companyName} />
          ))}
        </div>
      );
    }
    if (widgetType === 'list') {
      return (
        <div className="flex flex-col gap-3">
          {reviews.map((r) => (
            <Card key={r.id} review={r} accent={accent} showResponse={showCompanyResponse} companyName={companyName} />
          ))}
        </div>
      );
    }
    if (widgetType === 'cards') {
      return (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {reviews.map((r) => (
            <div key={r.id} className="flex-shrink-0 w-64">
              <Card review={r} accent={accent} showResponse={showCompanyResponse} companyName={companyName} />
            </div>
          ))}
        </div>
      );
    }
    // carousel
    return (
      <div>
        <Card review={reviews[safeIdx]} accent={accent} showResponse={showCompanyResponse} companyName={companyName} />
        {reviews.length > 1 && (
          <div className="flex items-center justify-center gap-3 mt-3">
            <button
              onClick={() => setIdx((i) => (i - 1 + reviews.length) % reviews.length)}
              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex gap-1.5 items-center">
              {reviews.map((_, i) => (
                <span
                  key={i}
                  className="h-2 rounded-full transition-all"
                  style={{ width: i === safeIdx ? 18 : 8, background: i === safeIdx ? accent : '#d1d5db' }}
                />
              ))}
            </div>
            <button
              onClick={() => setIdx((i) => (i + 1) % reviews.length)}
              className="w-7 h-7 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-5">
      <div className="text-center mb-4">
        {heading && <h3 className="text-lg font-bold text-gray-900">{heading}</h3>}
        {totalReviews > 0 && (
          <div className="flex items-center justify-center gap-2 mt-1">
            <Stars value={averageRating} />
            <span className="text-sm text-gray-600">
              {averageRating} based on {totalReviews} review{totalReviews === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>
      {body()}
      {ctaLabel && reviews.length > 0 && (
        <div className="text-center mt-4">
          <span
            className="inline-flex items-center px-5 py-2.5 rounded-full text-white text-sm font-semibold"
            style={{ background: accent }}
          >
            {ctaLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default ReviewsWidgetPreview;
